import React, {useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import constants from "./spacecast/config/Constants";
import SpacecastStats from "./components/SpacecastStats";
import Settings from "./components/Settings";
import Target from "./components/Target";
import Info from "./components/Info";
import Close from "./components/Close";
import Spacecast3dCanvas from "./spacecast/Spacecast";
import SpacecastError from "./spacecast/SpacecastError";


function Spacecast3d(props) {
  const { fileId, spacecastArray = [] } = props;
  const [showInfo, setShowInfo] = useState(false);
  const [showStopButton, setShowStopButton] = useState(false);
  const [targetName, setTargetName] = useState("planet|Earth");
  const [targetDistance, setTargetDistance] = useState(null);
  const [unitOfDistance, setUnitOfDistance] = useState("km");
  const spacecasts = useMemo(() => spacecastArray.filter(sc => !!sc.ra), [spacecastArray]);
  const [spacecastData, setSpacecastData] = useState(spacecasts.length === 0 ? null : [0]);
  const [error, setError] = useState(null);

  let lastAnimationTime = 0;
  const requestAnimationId = useRef(null);
  const mounted = useRef(false);

  const tryCatch = (func, final=()=>{}) => ((args) => {
    try{
      func(args)
    }catch (e){
      console.log(e, args)
      setError(e);
    }finally {
      final();
    }
  });

  const cleanup = tryCatch(() => {
      mounted.current = false;
      if(!!requestAnimationId.current) {
        tryCatch(() => window.cancelAnimationFrame(requestAnimationId.current));
      }
      if(!!window.spacecast?.renderer) {
        window.spacecast.renderer.dispose();
      }
    },
    () => document.getElementById("spacecast").innerHTML = ""
  );

  const initBeam = tryCatch(() => {
    if(!mounted.current){
      return;
    }
    const beam = window.spacecast?.universe?.beam;
    if(!beam){
      return;
    }
    if (spacecastData && !!window.spacecast) {
      const broadcastAt = new Date(spacecastData.spacecast_at);
      const rightAscension = parseFloat(spacecastData.ra) * (Math.PI/12); // convert right ascension (hours, full circle is 24h) to radians
      const declination = parseFloat(spacecastData.dec) * (Math.PI/180); // convert declination (degrees) to radians

      beam.setBroadcastAt(
        broadcastAt,
        rightAscension,
        declination
      );
      beam.show();
    } else {
      beam.hide();
    }
  });

  const loadCanvas = tryCatch(() => {
    cleanup();
    const spacecast3dCanvas = new Spacecast3dCanvas(
      document.getElementById("appContent")?.clientWidth || 0,
      window.innerHeight - (document.getElementById("appHeader")?.clientHeight || 0)
    );
    document
      .getElementById("spacecast")
      .appendChild(spacecast3dCanvas.renderer.domElement);
    window.spacecast = spacecast3dCanvas;
    spacecast3dCanvas.init();
    requestAnimationId.current = window.requestAnimationFrame(nextFrame);
    mounted.current = true;
    initBeam();
  });

  useEffect(() => {
    loadCanvas();
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(initBeam, [spacecastData]);

  const setSpacecastRecord = (rec) => {
    setSpacecastData({...rec, right_ascension:rec.ra, declination:rec.dec})
  };

  useEffect(tryCatch(() => {
    if(spacecasts.length > 0) {
      setSpacecastRecord(spacecasts[0]);
    }
  }), [spacecasts]);

  // Loop that renders the next frame.
  const nextFrame = tryCatch(currentTime => {
    if (window.spacecast) {
      const deltaTime = currentTime - lastAnimationTime;
      lastAnimationTime = currentTime;
      window.spacecast.update(currentTime, deltaTime);
      window.spacecast.renderer.render(
        window.spacecast.universe.three.scene,
        window.spacecast.camera.three.camera
      );
    }

    requestAnimationId.current = requestAnimationFrame(nextFrame);
  });

  useEffect(tryCatch(() => {
    if (!window.spacecast) {
      return;
    }
    window.spacecast.updateUiDistanceCallback = distanceUD => {
      let conversionFactor = constants.UD_TO_KM;
      let unitName = "kilometer";
      switch (unitOfDistance) {
        case "km":
          conversionFactor = constants.UD_TO_KM;
          unitName = "kilometers";
          break;
        case "mi":
          conversionFactor = constants.UD_TO_MILES;
          unitName = "miles";
          break;
        case "au":
          conversionFactor = constants.UD_TO_AU;
          unitName = "astronomical units";
          break;
        case "ly":
          conversionFactor = constants.UD_TO_LY;
          unitName = "light years";
          break;
        default:
          break;
      }
      const distanceString = (distanceUD * conversionFactor)
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      setTargetDistance(`${distanceString} ${unitName}`);
    };
  }), [unitOfDistance]);

  useEffect(tryCatch(() => {
    if (!window.spacecast) {
      return;
    }
    window.spacecast.updateUiDistance = showInfo;
  }), [showInfo]);

  return !!error ?
      <SpacecastError
        reload={() => {
          loadCanvas();
          setError(null);
        }}
      /> :
      <div style={{ width: "100dvw", left: 0, position:'absolute' }}>
        {!spacecastData ? null :
          <SpacecastStats
            spacecastData={spacecastData}
            setSpacecastData={setSpacecastRecord}
            spacecastArray={spacecastArray}
          />}
          <Target
            setShowInfo={setShowInfo}
            setShowStopButton={setShowStopButton}
            targetName={targetName}
            setTargetName={setTargetName}
          />
          <Info
            showInfo={showInfo}
            setShowInfo={setShowInfo}
            showStopButton={showStopButton}
            targetName={targetName}
            targetDistance={targetDistance}
          />
          <Settings
            unitOfDistance={unitOfDistance}
            setUnitOfDistance={setUnitOfDistance}
          />
          <Close fileId={fileId} />
      </div>;
}
const mapStateToProps = state => ({
  fileId: state.viewItem ? state.viewItem.id : null,
  spacecastArray: state.viewItem ? state.viewItem.spacecasts || [] : [],
  spacecastUpload: state.viewItem ? state.viewItem.spacecast_upload : null,
});

export default connect(mapStateToProps)(Spacecast3d);
